import { P } from '@/components/Atoms/P/P';
import { TimeAgo } from '@/components/Atoms/TimeAgo/TimeAgo';
import { TitleLink } from '@/components/Atoms/TitleLink/TitleLink';
import { CardActions, CardBody, CardDescription, ImageCard } from '@/components/Molecules/Card/Card';
import { ContentPin } from '@/components/Molecules/ContentPin/ContentPin';
import { ContentStatus } from '@/components/Molecules/ContentStatus/ContentStatus';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { SubscribeButton } from '@/components/Molecules/SubscribeButton/SubscribeButton';
import type { IPinableItem, ISeriesItem } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { useId } from 'react';
import styles from './SeriesCard.module.css';

interface ISeriesCard {
    series: ISeriesItem & IPinableItem;
    pinned?: boolean;
    withDescription?: boolean;
}

/**
 * A responsive card for a content item with a background image, type, title and save status.
 */
export const SeriesCard = ({ series, pinned, withDescription = true }: ISeriesCard) => {
    const titleId = useId();
    return (
        <ImageCard
            image={series.leadImage}
            data-cds-theme="dark"
            id={titleId}
            key={series.id}
            title={series.title}
            href={`/series/${series.id}`}
            prefix={
                <div className={styles.headerLine}>
                    <ContentTypeHeader type={ContentType.series} />
                    {(pinned || series.pinned) && <ContentPin />}
                    <ContentStatus status={series.contentStatus} />
                </div>
            }
            withImage={false}
        >
            <CardBody>
                {withDescription && series.description && (
                    <CardDescription lines={3}>{series.description}</CardDescription>
                )}
                {series.latestInsight && (
                    <P className={styles.insight}>
                        <TitleLink className={styles.titleLink} href={series.latestInsight.url}>
                            {series.latestInsight.title}
                        </TitleLink>
                        <TimeAgo datetime={series.latestInsight.displayDate} />
                    </P>
                )}
            </CardBody>
            {!series.isArchived && (
                <CardActions>
                    <SubscribeButton
                        aria-describedby={titleId}
                        series={series}
                        initialSubscriptionState={series.isSubscribed}
                    />
                </CardActions>
            )}
        </ImageCard>
    );
};
